import type { ReadFileResponse } from "@shared/types";
import { API, post } from "./api";
import { upload } from "@vercel/blob/client";
import { fromByteArray } from "base64-js";
import { trackEvent } from "./metrics";

const vercelFunctionBodySizeLimit = 4 * 1024 * 1024; // Vercel has 4.5 MB limit, 4 MB to be safe

function getFileExtension(fileName: string): string {
	const lastDot = fileName.lastIndexOf(".");
	return lastDot !== -1 ? fileName.slice(lastDot).toLowerCase() : "";
}

async function readTextFile(file: File): Promise<string> {
	return file.text();
}

async function getUploadToken(): Promise<string> {
	return fetch(`${API}/api/upload-token`, {
		method: "GET",
		headers: {
			"Content-Type": "application/json",
		},
	})
		.then((res) => res.json())
		.then((data) => data.token);
}

async function uploadFileToBlob(file: File): Promise<string> {
	const token = await getUploadToken();
	const blob = await upload(file.name, file, {
		access: "public",
		handleUploadUrl: `${API}/api/upload-url`,
		clientPayload: JSON.stringify({
			contentType: file.type,
			token,
		}),
	});

	return blob.url;
}

export async function readFile(
	file: File,
	maxSymbols = 10000,
): Promise<ReadFileResponse> {
	const ext = getFileExtension(file.name);
	let text = "";

	// Process TXT, and MD files client-side
	if (ext === ".txt" || ext === ".md") {
		text = await readTextFile(file);
	} else if (ext === ".docx" || ext === ".pdf") {
		let blobUrl: string | undefined;
		let base64: string | undefined;

		// Files larger than 4 MB are uploaded to blob
		if (file.size > vercelFunctionBodySizeLimit) {
			blobUrl = await uploadFileToBlob(file);
		} else {
			const arrayBuffer = await file.arrayBuffer();
			const bytes = new Uint8Array(arrayBuffer);
			base64 = fromByteArray(bytes);
		}
		try {
			const t0 = performance.now();
			// Send blob URL or file to read-file endpoint
			const result = await post<ReadFileResponse>("read-file", {
				blobUrl,
				file: base64,
				fileName: file.name,
				maxSymbols,
			}).then((response) => {
				const t1 = performance.now();

				trackEvent({
					event: "read_file_responded",
					metadata: {
						time: t1 - t0,
						size: file.size,
					},
				});
				return response;
			});

			return result;
		} catch (error) {
			throw new ClientReadFileError("Failed to read file content", error);
		}
	} else {
		throw new ClientReadFileError("Unsupported file type", { ext });
	}

	// Apply maxSymbols limit for client-side processed files
	const limitedText = text.slice(0, maxSymbols);

	return { text: limitedText };
}

class ClientReadFileError extends Error {
	cause: unknown;
	constructor(message: string, cause?: unknown) {
		super(message);
		this.name = "ClientReadFileError";
		this.cause = cause;
	}
}
